import React, { useContext, useEffect, useRef, useState } from 'react';
import { bool, func, shape, string } from 'prop-types';
import axios from 'axios';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { useCollectionDetail, useProduct } from '@thd-nucleus/data-sources';
import Helper from './Helper';
import './media-gallery.style.scss';
import { componentReady } from '../publisher';
import { MediaGalleryMobile } from './subcomponents/MediaGalleryMobile';
import { MediaGalleryDesktop } from './subcomponents/MediaGalleryDesktop';
import { MediaHighlight } from './subcomponents/MediaHighlight/MediaHighlight';
import { MediaGalleryLoader } from './subcomponents/MediaGalleryLoader/MediaGalleryLoader';

export const MediaGallery = ({
  collectionId, doubleTap, hideProductImagesLabel, highlight, highRes, hover, itemId, onMediaClick, media, showLoader, showFavorites
}) => {
  const mediaMapRef = useRef(null);
  const [highResImages, setHighResImages] = useState([]);
  const highResImageRef = useRef(itemId);
  const experienceContext = useContext(ExperienceContext);
  const { channel } = experienceContext;
  const isMobile = channel === 'mobile';
  const storeId = useStoreId();
  const [hoverImageUrl, setHoverImage] = useState('');

  useEffect(() => {
    if (hover) {
      window.LIFE_CYCLE_EVENT_BUS.on('supersku.hover', (imgUrl) => {
        setHoverImage(imgUrl?.output);
      });
    }
  }, []);

  useEffect(() => {
    if (itemId) {
      componentReady({ itemId });
    }
  }, []);

  const { data, error, loading } = useProduct({ itemId, domains: ['identifiers', 'media'], skip: !itemId });

  const collectionData = useCollectionDetail({ collectionId, storeId, skip: !collectionId });
  const apiState = media ? null : Helper.getAPIState({
    data: collectionId ? collectionData.data : data,
    loading: collectionId ? collectionData.loading : loading,
    error: collectionId ? collectionData.error : error,
    fetchId: itemId || collectionId
  });

  const { product } = (data || {});
  const { collection } = (collectionData || {}).data || {};
  const { metadata } = collection || {};
  const mediaSource = collectionId ? (collection || {}).media : (product || {}).media;
  const mediaList = Helper.getInitialMediaList({ media: media || mediaSource });

  const thumbnailSize = isMobile ? '100' : '145';
  const thumbnails = Helper.getStructuredMediaList({ mediaList, size: thumbnailSize });
  const main = Helper.getStructuredMediaList({ mediaList, size: '600' });
  const zoom = Helper.getStructuredMediaList({ mediaList, size: '1000' });
  const displayedMedia = {
    thumbnails,
    main,
    zoom,
  };

  useEffect(() => {
    // TODO: Hook
    if (channel !== 'mobile' && highRes) {
      const checkValidityPromise = [];
      zoom.forEach((zoomedImage) => {
        const imageCheckPromise = Helper.getHighResImages(zoomedImage, axios);
        checkValidityPromise.push(imageCheckPromise);
      });
      Promise.all(checkValidityPromise).then((responses) => {
        const images = responses.filter((resp) => !!resp);
        if (images.length) {
          setHighResImages(images);
        }

      }).catch(() => {});
    }
  }, [itemId]);

  if (apiState === 'loading') {
    return showLoader && <MediaGalleryLoader isMobile={isMobile} />;
  }

  if (apiState === 'error' || !mediaList?.length) {
    return null;
  }

  if (highlight) {
    return <MediaHighlight itemId={itemId} />;
  }

  const handleMediaClick = ({ media: clickedMedia }) => {
    if (onMediaClick) {
      onMediaClick({ media: clickedMedia });
    }
  };

  mediaMapRef.current = displayedMedia;

  if (highResImages) {
    mediaMapRef.current = {
      ...mediaMapRef.current,
      highResolution: highResImages
    };
  }

  return (
    <div>
      {isMobile ? (
        <MediaGalleryMobile
          media={mediaMapRef.current}
          onMediaClick={handleMediaClick}
          product={product || metadata}
          showFavorites={showFavorites}
        />
      ) : (
        <MediaGalleryDesktop
          media={mediaMapRef.current}
          collection={collection}
          product={product}
          totalThumbnailsToShow={6}
          hideProductImagesLabel={hideProductImagesLabel}
          showFavorites={showFavorites}
          hoverImageUrl={hoverImageUrl}
        />
      )}
    </div>
  );
};

MediaGallery.displayName = 'MediaGallery';

MediaGallery.propTypes = {
  collectionId: string,
  doubleTap: bool,
  hideProductImagesLabel: bool,
  highlight: bool,
  highRes: bool,
  hover: bool,
  itemId: string,
  onMediaClick: func,
  media: shape({}),
  showLoader: bool,
  showFavorites: bool
};

MediaGallery.defaultProps = {
  collectionId: null,
  doubleTap: false,
  hideProductImagesLabel: false,
  highlight: false,
  highRes: false,
  hover: false,
  itemId: null,
  onMediaClick: null,
  media: null,
  showLoader: false,
  showFavorites: false
};
